import Styles from './FormatBar.module.scss'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { FormatBarProps } from './Types'
import { Alignment, AlignmentButton } from './AlignmentButton'
import { CommentButton } from './CommentButton'
import { DownloadButton } from './DownloadButton'
import { MoreButton } from './MoreButton'
import { InlineFormatHeader } from 'components/InlineFormatBar/components/InlineFormatHeader/InlineFormatHeader'
import { LinkMetaData } from '../LinkMetaData/LinkMetaData'
import { useState } from 'react'
import { LinkForm } from '../LinkForm/LinkForm'
import { makeUrl } from 'common/Prose/utils/link'
import { PromptInput } from './PromptInput/PromptInput'
import { Unsplash } from 'proseShared/IntegrationsIcons'
import { openImageReplacePrompt } from '../../openImageReplacePrompt'
import { useFocusWithin } from '@react-aria/interactions'
import Lightbox from 'react-image-lightbox'
import { optimizedImageUrlString } from 'utils'
import { useModals } from 'almanac-ui/Modal'
import { Change } from './Change'
import { useDocStore } from 'stores/DocStore'
import { observer } from 'mobx-react-lite'
import { InlineFormatFooter } from 'components/InlineFormatBar/components/InlineFormatFooter/InlineFormatFooter'
import { AttributionButton } from './AttributionButton'

const FormatBar = observer(({ view, getPos, attrs, showOnlyComment = false }: FormatBarProps) => {
  const link = attrs.href
  const docStore = useDocStore()
  const { rootStore } = docStore
  const { featureFlags } = rootStore
  const [isEditingLink, setIsEditingLink] = useState(false)
  const [isPromptFocused, setIsPromptFocused] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [showFullSize, setShowFullSize] = useState(false)
  const stackManager = useModals()
  const isImageRevisionsEnabled = featureFlags.isActive('image_revisions')
  const { imageStores } = docStore
  const imageStore = imageStores.get(attrs.src)
  const { mediaFileId } = attrs

  const { focusWithinProps: promptInputFocusWithinProps } = useFocusWithin({
    onFocusWithinChange: (focused) => {
      setIsPromptFocused(focused)
    },
  })

  if (!!imageStore?.variants.length) return null

  const isUsingPrompt = isPromptFocused || prompt !== ''

  const updateLink = (newUrl?: string) => {
    const tr = view.state.tr
    tr.setNodeMarkup(getPos(), undefined, { ...attrs, href: newUrl && makeUrl(newUrl) })
    view.dispatch(tr)
  }

  const deleteImage = () => {
    const tr = view.state.tr
    tr.delete(getPos(), getPos() + 1)
    view.dispatch(tr)
  }

  const handlePromptSubmit = () => {
    openImageReplacePrompt(view, attrs, {
      initialQuery: prompt,
      preselectedIntegration: Unsplash,
      stackManager,
    })
  }

  if (showOnlyComment) {
    return (
      <InlineFormatBar className={Styles.wrapper} contentClassName={Styles.buttons}>
        <CommentButton view={view} />
      </InlineFormatBar>
    )
  }

  return (
    <>
      {showFullSize && (
        <Lightbox
          mainSrc={optimizedImageUrlString(attrs.src)}
          onCloseRequest={() => setShowFullSize(false)}
          reactModalProps={{ preventScroll: true }}
        />
      )}
      <InlineFormatBar className={Styles.wrapper} contentClassName={Styles.buttons}>
        {!isUsingPrompt && link && !isEditingLink && (
          <InlineFormatHeader>
            <LinkMetaData
              url={link}
              isEditEnabled={view.editable}
              onEdit={() => setIsEditingLink(true)}
              onDelete={() => updateLink(undefined)}
            />
          </InlineFormatHeader>
        )}

        {!isUsingPrompt && isEditingLink && (
          <InlineFormatHeader>
            <LinkForm
              url={link}
              isEditing={Boolean(link)}
              onSave={(newUrl) => {
                updateLink(newUrl)
                setIsEditingLink(false)
              }}
              onCancel={() => setIsEditingLink(false)}
            />
          </InlineFormatHeader>
        )}

        <div {...promptInputFocusWithinProps} className={Styles.promptContainer}>
          <PromptInput
            value={prompt}
            onChange={setPrompt}
            onSubmit={handlePromptSubmit}
            isFocused={isPromptFocused}
            view={view}
            attrs={attrs}
            getPos={getPos}
          />
        </div>

        {!isUsingPrompt && (
          <>
            <AlignmentButton
              value={attrs.float as Alignment}
              onChange={(value) => {
                const tr = view.state.tr
                tr.setNodeMarkup(getPos(), undefined, { ...attrs, float: value })
                view.dispatch(tr)
              }}
            />
            <CommentButton view={view} />
            {mediaFileId && <AttributionButton mediaFileId={mediaFileId} />}
            <DownloadButton url={attrs.src} />
            <MoreButton
              view={view}
              attrs={attrs}
              onAction={(action) => {
                if (action === 'zoom') {
                  setShowFullSize(true)
                }

                if (action === 'delete') {
                  deleteImage()
                }

                if (action === 'caption') {
                  const tr = view.state.tr
                  tr.setNodeMarkup(getPos(), undefined, { ...attrs, description: 'Caption here' })
                  view.dispatch(tr)
                }

                if (action === 'link') {
                  if (link) {
                    updateLink(undefined)
                  } else {
                    setIsEditingLink(true)
                  }
                }
              }}
            />
          </>
        )}
        {isImageRevisionsEnabled && (
          <InlineFormatFooter>
            <Change
              attrs={attrs}
              onChange={() => {
                const tr = view.state.tr
                view.dispatch(tr)
              }}
            />
          </InlineFormatFooter>
        )}
      </InlineFormatBar>
    </>
  )
})

export { FormatBar }
